import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DesignAngularKitModule,
  ItModalComponent,
  ItNotificationService,
  NotificationPosition,
} from 'design-angular-kit';
import { ChangeOwnershipComponent } from '../../../../components/change-ownership/change-ownership.component';
import { ChangeSettingComponent } from '../../../../components/change-setting/change-setting.component';
import { RequestSummaryComponent } from '../../../../components/request-summary/request-summary.component';
import { DischargeInfo, DischargeInfoDto, RequestDto } from '../../../../models/request.dto';
import { AuthService } from '../../../../services/auth.service';
import { NoteService } from '../../../../services/note.service';
import { RequestService } from '../../../../services/request.service';
import { StructureService } from '../../../../services/structure.service';
import { CloseRequestModalComponent } from '../close-request-modal/close-request-modal.component';
import { CompleteRequestModalComponent } from '../complete-request-modal/complete-request-modal.component';
import { DeleteRequestModalComponent } from '../delete-request-modal/delete-request-modal.component';
import { DischargeInfoComponent } from '../discharge-info/discharge-info.component';
import { format } from 'date-fns';

@Component({
  selector: 'app-request-action',
  standalone: true,
  imports: [
    CommonModule,
    DesignAngularKitModule,
    ChangeOwnershipComponent,
    RequestSummaryComponent,
    CompleteRequestModalComponent,
    DeleteRequestModalComponent,
    ChangeSettingComponent,
    FormsModule,
    DischargeInfoComponent,
    CloseRequestModalComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './request-action.component.html',
  styleUrl: './request-action.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class RequestActionComponent {
  @ViewChild('changeOwnerShipModal') changeOwnerShipModal!: ItModalComponent;
  @ViewChild('changeSettingModal') changeSettingModal!: ItModalComponent;
  @ViewChild('completeRequestModal') completeRequestModal!: ItModalComponent;
  @ViewChild('deleteRequestModal') deleteRequestModal!: ItModalComponent;
  @ViewChild('confirmModal') confirmModal!: ItModalComponent;
  @ViewChild('closeRequestModal') closeRequestModal!: ItModalComponent;

  confirmMessage = '';
  private pendingStatusChange: string | null = null;

  @Input() request!: RequestDto;
  @Input() requestDates: DischargeInfo[] = [];
  @Input() isConsentMissing: boolean | undefined;
  @Output() refreshRequest = new EventEmitter<any>();

  structureTypes: any[] = [];
  structureTypesVisible: any[] = [];
  allTypeVisible: boolean = false;
  structures: any[] = [];
  searchTerm: string = '';
  filteredStructures: any[] = [];
  selectedStructure: any = null;
  structureRequestDate: Date | null = null;
  structureTypeFiltered: any[] = [];

  constructor(
    private structureService: StructureService,
    private requestService: RequestService,
    public authService: AuthService,
    private notification: ItNotificationService,
    private noteService: NoteService
  ) {}

  getStructureTypes() {
    this.structureService.getStructureTypes().subscribe((results) => {
      this.structureTypes = results;
      this.structureTypesVisible = results.filter((t: { subSettings: any[]; }) => t.subSettings.find((s: { id: number; visible: boolean; })=> s.id === this.request.setting.subSettings[0].id ));
      this.structureTypeFiltered = results.filter((t: { subSettings: any[]; }) => t.subSettings.find((s: { id: number; visible: boolean; })=> s.id === this.request.setting.subSettings[0].id ));
      if (this.structureTypesVisible.length) {
        this.structureService
          .getStructuresFromType(this.structureTypesVisible[0].code)
          .subscribe((structures) => {
            this.structures = structures;
            this.filterStructures();
          });
      }
    });
  }

  getTab(event: any) {
    const index = parseInt(event.id.replace('tab_', ''), 10);
    const typeId = this.allTypeVisible ? this.structureTypes[index].code : this.structureTypesVisible[index].code;
    this.structureService
      .getStructuresFromType(typeId)
      .subscribe((structures) => {
        this.structures = structures;
        this.filterStructures();
      });
  }

  filterStructures() {
    const term = this.searchTerm.toLowerCase();
    this.filteredStructures = this.structures.filter(
      (struct) =>
        (struct.address && struct.address.toLowerCase().includes(term)) ||
        (struct.name && struct.name.toLowerCase().includes(term))
    );
    this.selectedStructure = null;
  }

  resetFilter() {
    this.searchTerm = '';
    this.filterStructures();
  }

  selectStructure(structure: any) {
    this.selectedStructure = structure;
  }

  onSubmitStructure() {
    if (!this.selectedStructure) {
      alert('Seleziona una struttura prima di confermare');
      return;
    }

    this.structureService
      .setStructureForTransition(this.request.uuid, this.selectedStructure.code)
      .subscribe((result) => {
        if (this.request.setting.code === 'HCP' || this.request.setting.code === '26' || this.request.setting.code === 'ADI' || this.request.setting.code === 'RSA') {
          if (this.request.status.code === 'VAL') {
            this.updateRequestStatus('CPZ');
          }
        } else if (this.request.setting.code === 'PA' || this.request.setting.code === 'ODC') {
          if (this.request.status.code === 'TPA') {
            this.updateRequestStatus('CPZ');
          }
        } else {
          if (this.request.status.code === 'PIC') {
            this.updateRequestStatus('CS');
          }
        }
        this.updateStructureRequestDate();
      });
  }

  isDatePresent(dateType: string): boolean {
    return this.requestDates.find(d => d.dateType.code == dateType) ? true : false;
  }

  updateStructureRequestDate() {

    let payload : DischargeInfoDto = {
      date: this.structureRequestDate!.toString()+'T00:00:00',
      operatorCode: this.authService.userInfo.username,
      transitionId: this.request.id,
      typeId: 3
    };
    if(this.requestDates && this.requestDates.find(d => d.dateType?.code == 'DPS')) {
      payload.id = this.requestDates.find(d => d.dateType?.code == 'DPS')?.id;
      this.requestService
      .updateDischargeInfo(payload)
      .subscribe();
    } else {
      this.requestService
      .createDischargeInfo(payload)
      .subscribe();
    }
    
  }

  confirmStatusChange(status: string) {
    this.pendingStatusChange = status;
    this.confirmMessage = `Questa operazione modificherà lo stato della pratica. Vuoi procedere?`;
    this.confirmModal.toggle();
  }

  updateRequestStatus(status: string, refresh?: boolean) {
    const message =
      this.statusMessages[status] || 'Stato aggiornato con successo.';
    this.requestService.changeStatus(this.request.uuid, status).subscribe(() => {
      this.notification.success(
        'Pratica aggiornata.',
        message,
        true,
        3000,
        NotificationPosition.Top
      );
      if(refresh || refresh === undefined) {
        this.refreshRequest.emit();
      }
    });
  }

  proceedConfirmation() {
    if (this.pendingStatusChange) {
      this.updateRequestStatus(this.pendingStatusChange);
      this.pendingStatusChange = null;
    }
    this.confirmModal.toggle();
  }

  cancelConfirmation() {
    this.pendingStatusChange = null;
    this.confirmModal.toggle();
  }

  delete(note: string) {
    const request = {
      transitionId: this.request.id,
      insertUserCode: this.authService.userInfo.username,
      note,
    };
    this.requestService.deleteRequest(request).subscribe(() => {
      this.notification.success(
        'Pratica aggiornata.',
        'Richiesta annullata correttamente.',
        true,
        3000,
        NotificationPosition.Top
      );
      this.refreshRequest.emit();
    });
  }

  insertNoteForDischargeDate(note: string | null) {
    const request = {
          transitionId: this.request.id,
          typeId: '5',
          text: 'Aggiornamento data effettiva dimissione' + (note ? ' per: ' + note : ''),
          insertUserCode: this.authService.userInfo.username,
          title: 'Data effettiva dimissione'
        }
        this.noteService.insertNote(request).subscribe( result => {
          this.refreshRequest.emit();
        });
  }

  completeRequest(dischargeDate: Date | null, dischargeTime: string|null, note: string | null) {
    let dischargeDateTime = format(dischargeDate!,'yyyy-MM-dd').toString()+'T'+ dischargeTime+':00';
    let payload: DischargeInfoDto = {
      date: dischargeDateTime,
      operatorCode: this.authService.userInfo.username,
      transitionId: this.request.id,
      typeId: 2
    };
    if(this.requestDates && this.requestDates.find(d => d.dateType?.code == 'DDE')) {
      payload.id = this.requestDates.find(d => d.dateType?.code == 'DDE')?.id;
      this.requestService
      .updateDischargeInfo(payload)
      .subscribe(() => {
        if (this.request.status.code !== 'END') {
          this.updateStatusAndInsertNote(note);
        } else {
          this.insertNoteForDischargeDate(note);
        }
      });
    } else {
      this.requestService
      .createDischargeInfo(payload)
      .subscribe(() => {
        if (this.request.status.code !== 'END') {
          this.updateStatusAndInsertNote(note);
        } else {
          this.insertNoteForDischargeDate(note);
        }
      });
    }
  }

  updateStatusAndInsertNote(note: string | null) {
      this.requestService.changeStatus(this.request.uuid, 'END').subscribe(() => {
      this.notification.success(
        'Pratica aggiornata.',
        'Richiesta completata con successo.',
        true,
        3000,
        NotificationPosition.Top
      );
      this.insertNoteForDischargeDate(note);
    });
  }

  reopenRequest() {
    this.requestService.reopenRequest(this.request.uuid).subscribe(result => {
      this.refreshRequest.emit();
    })
  }

  closeRequest() {
    this.confirmStatusChange('CHI');
  }

  presaInCarico() {
    this.confirmStatusChange('PIC');
  }

  cambioCompetenza(event: any) {
    const request = {
      transitionId: this.request.id,
      actorId: event.actorId,
      actorReferenceId: event.ownershipId,
      insertUserCode: this.authService.userInfo.username,
    };
    this.requestService.changeOwnership(request).subscribe(() => {
      if (this.request.status.code === 'DAT' ) {
        if(this.request.setting.code === 'HCP' || this.request.setting.code === '26' || this.request.setting.code === 'ADI' || this.request.setting.code === 'RSA') {
          this.updateRequestStatus('TPV');
        } else {
          this.updateRequestStatus('TPA');
        }
      } else {
      this.notification.success(
        'Cambio competenza.',
        'Il cambio di competenza è stato completato con successo.',
        true,
        3000,
        NotificationPosition.Top
      );
      this.refreshRequest.emit();
      }
    });
  }

  cambioSetting(event: any) {
    this.requestService
      .changeSetting(this.request.id, event.selectedSubsetting)
      .subscribe(() => {
        this.refreshRequest.emit();
        this.notification.success(
          'Cambio setting.',
          'Il cambio di setting è stato completato con successo.',
          true,
          3000,
          NotificationPosition.Top
        );
      });
  }

  trasmetti() {
    this.confirmStatusChange('NEW');
  }

  changeOwnerShipConfirmed(event: any) {
    this.cambioCompetenza(event);
    this.changeOwnerShipModal.toggle();
  }

  cancelledChangeOwnerShip() {
    this.changeOwnerShipModal.toggle();
  }

  changeSettingConfirmed(event: any) {
    this.cambioSetting(event);
    this.changeSettingModal.toggle();
  }

  cancelledChangeSetting() {
    this.changeSettingModal.toggle();
  }

  requestConfirmed(event: { dischargeDate: Date | null, dischargeTime: string | null, note: string | null }) {
    this.completeRequest(event.dischargeDate, event.dischargeTime, event.note);
    this.completeRequestModal.toggle();
  }

  cancelledCompleteRequest() {
    this.completeRequestModal.toggle();
  }

  requestDeleted(event: { note: string }) {
    this.delete(event.note);
    this.deleteRequestModal.toggle();
  }

  cancelledDeleteRequest() {
    this.deleteRequestModal.toggle();
  }

  cancelledCloseRequest() {
    this.closeRequestModal.toggle();
  }

  requestClosed(event: { note: string })  {
    const request = {
          transitionId: this.request.id,
          typeId: '5',
          text: 'Stato modificato in CHIUSO per ' + event.note,
          insertUserCode: this.authService.userInfo.username,
          title: 'Cambio stato'
        }
        this.noteService.insertNote(request).subscribe( result => {
          this.updateRequestStatus('CHI');
        });
    this.closeRequestModal.toggle();
  }

  private statusMessages: { [key: string]: string } = {
    CS: 'Struttura confermata con successo.',
    DEL: 'Richiesta annullata correttamente.',
    END: 'Richiesta completata con successo.',
    CP: 'Paziente confermato con successo.',
    PIC: 'Presa in carico effettuata con successo.',
    NEW: 'Richiesta trasmessa con successo.',
  };
}
